import React, { useState, useEffect } from "react";
import { FiUpload, FiExternalLink, FiCopy, FiCheck, FiMapPin, FiClock } from "react-icons/fi";
import { useComponentTranslation } from "@/services/i18n";
import api from "@/services/api";
import useStore from "@/services/store";
import toast from "react-hot-toast";
import Loader from "@/components/loader";
import { readFileAsync } from "@/utils";

const PRESET_COLORS = [
  "#6C4CFF",
  "#E8613C",
  "#34A853",
  "#4A9EF5",
  "#F5A623",
  "#1A1A1A",
];

const translations = {
  en: {
    title: "Career Page",
    subtitle: "Changes appear live in the preview →",
    slug_label: "URL Slug",
    name_label: "Company Name",
    name_placeholder: "Your Company Name",
    color_label: "Brand Color",
    description_label: "Description",
    description_placeholder: "Tell candidates about your company...",
    vision_label: "Vision",
    vision_placeholder: "Describe your company vision...",
    mission_label: "Mission",
    mission_placeholder: "Describe your company mission...",
    logo_label: "Logo",
    banner_label: "Banner Image",
    banner_helper: "Recommended: 1920×400px",
    upload: "Upload",
    remove: "Remove",
    cta: "Save Changes",
    saving: "Saving...",
    success: "Career page updated!",
    error: "Error saving changes",
    error_slug: "Please enter a URL slug",
    error_name: "Please enter a company name",
    live_preview: "LIVE PREVIEW",
    hiring: "We're hiring!",
    open_positions: "Open Positions",
    apply: "Apply",
    no_positions: "No open positions yet",
    full_time: "Full-time",
  },
  fr: {
    title: "Page Carrière",
    subtitle: "Les changements apparaissent en direct dans l'aperçu →",
    slug_label: "Slug URL",
    name_label: "Nom de l'Entreprise",
    name_placeholder: "Nom de votre entreprise",
    color_label: "Couleur de Marque",
    description_label: "Description",
    description_placeholder: "Parlez aux candidats de votre entreprise...",
    vision_label: "Vision",
    vision_placeholder: "Décrivez la vision de votre entreprise...",
    mission_label: "Mission",
    mission_placeholder: "Décrivez la mission de votre entreprise...",
    logo_label: "Logo",
    banner_label: "Image de Bannière",
    banner_helper: "Recommandé : 1920×400px",
    upload: "Télécharger",
    remove: "Supprimer",
    cta: "Enregistrer",
    saving: "Enregistrement...",
    success: "Page carrière mise à jour !",
    error: "Erreur lors de l'enregistrement",
    error_slug: "Veuillez entrer un slug URL",
    error_name: "Veuillez entrer un nom d'entreprise",
    live_preview: "APERÇU EN DIRECT",
    hiring: "Nous recrutons !",
    open_positions: "Postes Ouverts",
    apply: "Postuler",
    no_positions: "Aucun poste ouvert",
    full_time: "Temps plein",
  },
};

function ColorSwatch({ color, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(color)}
      className={`w-10 h-10 rounded-lg transition-all duration-150 ${
        selected ? "ring-2 ring-offset-2 ring-gray-900 scale-110" : "hover:scale-105"
      }`}
      style={{ backgroundColor: color }}
    />
  );
}

function LivePreview({ slug, name, description, primaryColor, logoPreview, bannerPreview, jobs, t }) {
  const initial = (name || "C").charAt(0).toUpperCase();
  const previewUrl = `jobego.co/embed/${slug || "your-company"}`;

  return (
    <div className="sticky top-8">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">{t("live_preview")}</p>

      {/* Mock Browser Chrome */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Browser Bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="px-4 py-1 bg-white rounded-md text-xs text-gray-500 border border-gray-200 max-w-[240px] truncate">
              {previewUrl}
            </div>
          </div>
        </div>

        {/* Preview Content */}
        <div className="max-h-[600px] overflow-y-auto">
          {/* Hero Header */}
          <div
            className="px-8 pt-10 pb-8 text-center"
            style={{
              background: bannerPreview
                ? `url(${bannerPreview}) center/cover no-repeat`
                : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
            }}
          >
            {logoPreview ? (
              <div className="w-16 h-16 rounded-full bg-white mx-auto mb-4 overflow-hidden shadow-md flex items-center justify-center">
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-white shadow-md"
                style={{ backgroundColor: `${primaryColor}88`, border: "3px solid rgba(255,255,255,0.3)" }}
              >
                {initial}
              </div>
            )}
            <h2 className="text-xl font-bold text-white mb-1">{name || "Company Name"}</h2>
            <p className="text-sm text-white/80">{t("hiring")}</p>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {description && <p className="text-sm text-gray-600 leading-relaxed mb-6">{description}</p>}

            {/* Open Positions */}
            <h3 className="text-base font-bold text-gray-900 mb-4">{t("open_positions")}</h3>

            {jobs.length > 0 ? (
              <div className="space-y-3">
                {jobs.slice(0, 5).map((job) => (
                  <div key={job._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{job.job_title || job.title || "Untitled"}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {job.job_location || "Remote"}
                        {job.job_employment_type ? ` · ${job.job_employment_type === "FULL_TIME" ? t("full_time") : job.job_employment_type}` : ""}
                      </p>
                    </div>
                    <span
                      className="px-4 py-1.5 text-xs font-semibold rounded-full text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {t("apply")}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-400">{t("no_positions")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Career() {
  const { t } = useComponentTranslation(translations);
  const { organization, setOrganization } = useStore();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);

  const [slug, setSlug] = useState(organization?.slug || "");
  const [name, setName] = useState(organization?.name || "");
  const [description, setDescription] = useState(organization?.description || "");
  const [vision, setVision] = useState(organization?.vision || "");
  const [mission, setMission] = useState(organization?.mission || "");
  const [primaryColor, setPrimaryColor] = useState(organization?.career_website_color_primary || "#6C4CFF");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(organization?.logo || null);
  const [banner, setBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(organization?.career_website_banner || null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const { ok, data } = await api.post("/job/search", { organization_id: organization?._id, status: "active", limit: 5 });
        if (ok && data) setJobs(data);
      } catch (e) {
        console.error("Failed to load jobs for preview", e);
      }
    };
    fetchJobs();
  }, [organization?._id]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const previewData = await readFileAsync(file);
      setLogoPreview(previewData);
      const rawBody = await readFileAsync(file);
      const response = await api.post("/file", { file: { rawBody, name: file.name }, folder: "/career" });
      if (!response.ok) throw response;
      setLogo(response.data);
      setLogoPreview(response.data);
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
      setLogoPreview(organization?.logo || null);
      setLogo(null);
    } finally {
      setUploading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const previewData = await readFileAsync(file);
      setBannerPreview(previewData);
      const rawBody = await readFileAsync(file);
      const response = await api.post("/file", { file: { rawBody, name: file.name }, folder: "/career" });
      if (!response.ok) throw response;
      setBanner(response.data);
      setBannerPreview(response.data);
    } catch (error) {
      console.error("Banner upload error:", error);
      toast.error("Failed to upload banner");
      setBannerPreview(organization?.career_website_banner || null);
      setBanner(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!slug.trim()) return toast.error(t("error_slug"));
    if (!name.trim()) return toast.error(t("error_name"));

    setSaving(true);
    try {
      const response = await api.put("/organization", {
        organization_id: organization._id,
        slug: slug.toLowerCase().replace(/\s+/g, "-"),
        name,
        description,
        vision,
        mission,
        logo: logo || logoPreview,
        career_website_banner: banner || bannerPreview,
        career_website_color_primary: primaryColor,
      });
      if (!response.ok) throw response;
      setOrganization(response.data);
      toast.success(t("success"));
    } catch (e) {
      console.error(e);
      toast.error(t("error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex gap-10 min-h-[calc(100vh-130px)]">
      {/* Left Panel - Form */}
      <div className="flex-1 max-w-xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("subtitle")}</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* URL Slug */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">{t("slug_label")}</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="input w-full"
              required
            />
            <p className="text-xs text-gray-400 mt-1.5">https://jobego.co/embed/{slug || "your-company"}</p>
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">{t("name_label")}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("name_placeholder")}
              className="input w-full"
              required
            />
          </div>

          {/* Brand Color */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">{t("color_label")}</label>
            <div className="flex items-center gap-3">
              {PRESET_COLORS.map((color) => (
                <ColorSwatch key={color} color={color} selected={primaryColor === color} onClick={setPrimaryColor} />
              ))}
              <div className="relative">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="absolute inset-0 w-10 h-10 opacity-0 cursor-pointer"
                />
                <div
                  className={`w-10 h-10 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400 transition-colors ${
                    !PRESET_COLORS.includes(primaryColor) ? "ring-2 ring-offset-2 ring-gray-900" : ""
                  }`}
                  style={!PRESET_COLORS.includes(primaryColor) ? { backgroundColor: primaryColor, borderStyle: "solid", borderColor: primaryColor } : {}}
                >
                  {PRESET_COLORS.includes(primaryColor) && <span className="text-lg">+</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">{t("description_label")}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("description_placeholder")}
              className="input w-full"
              rows={4}
            />
          </div>

          {/* Vision */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">{t("vision_label")}</label>
            <textarea
              value={vision}
              onChange={(e) => setVision(e.target.value)}
              placeholder={t("vision_placeholder")}
              className="input w-full"
              rows={3}
            />
          </div>

          {/* Mission */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">{t("mission_label")}</label>
            <textarea
              value={mission}
              onChange={(e) => setMission(e.target.value)}
              placeholder={t("mission_placeholder")}
              className="input w-full"
              rows={3}
            />
          </div>

          {/* Logo & Banner */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            {/* Logo */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">{t("logo_label")}</label>
              {uploading ? (
                <div className="flex items-center justify-center w-full h-28 border border-gray-200 rounded-xl bg-gray-50">
                  <Loader size="small" />
                </div>
              ) : logoPreview ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center w-full h-28 border border-gray-200 rounded-xl bg-gray-50 p-2">
                    <img src={logoPreview} alt="Logo" className="max-h-full max-w-full object-contain" />
                  </div>
                  <button type="button" onClick={() => { setLogo(null); setLogoPreview(null); }} className="text-xs text-red-500 hover:text-red-600">
                    {t("remove")}
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors">
                  <FiUpload className="w-5 h-5 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">{t("upload")}</span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                </label>
              )}
            </div>

            {/* Banner */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("banner_label")}
              </label>
              {uploading ? (
                <div className="flex items-center justify-center w-full h-28 border border-gray-200 rounded-xl bg-gray-50">
                  <Loader size="small" />
                </div>
              ) : bannerPreview ? (
                <div className="space-y-2">
                  <img src={bannerPreview} alt="Banner" className="w-full h-28 object-cover border border-gray-200 rounded-xl" />
                  <button type="button" onClick={() => { setBanner(null); setBannerPreview(null); }} className="text-xs text-red-500 hover:text-red-600">
                    {t("remove")}
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors">
                  <FiUpload className="w-5 h-5 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">{t("upload")}</span>
                  <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" disabled={uploading} />
                </label>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <button type="submit" disabled={saving} className="btn btn-primary w-full">
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader size="small" color="white" />
                  {t("saving")}
                </span>
              ) : (
                t("cta")
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Right Panel - Live Preview */}
      <div className="flex-1 max-w-md">
        <LivePreview
          slug={slug}
          name={name}
          description={description}
          primaryColor={primaryColor}
          logoPreview={logoPreview}
          bannerPreview={bannerPreview}
          jobs={jobs}
          t={t}
        />
      </div>
    </div>
  );
}
